import { Divider, Tooltip } from 'antd';
import Col from 'antd/lib/col';
import Row from 'antd/lib/row';
import Space from 'antd/lib/space';
import Typography from 'antd/lib/typography';
import Empty from 'antd/lib/empty';
import { theme } from 'antd/lib';
import { EChartsOption } from 'echarts';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import { LineChart } from 'echarts/charts';
import { DataZoomComponent, GridComponent, MarkAreaComponent, TooltipComponent } from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import isNil from 'lodash/isNil';
import { useMemo } from 'react';
import { currencyUSDFormatter } from '../../domain/common/currencyFormatters';
import { ISavingsMetrics } from '../../domain/price-response/interface';
import { ContentBlock } from '../ContentBlock/ContentBlock';
import { ReactComponent as MoneyIcon } from '../icons/money.svg';
import { DEFAULT_DATE_FORMAT, getTimezoneFormattedTime } from '../../domain/date/date';
import { PriceResponseMarket } from '../../pages/priceResponse/view/scenarios/PriceResponseMarket';

const CHART_GROUP_ID = 'price-response-analytic-charts';

echarts.connect(CHART_GROUP_ID);

echarts.use([LineChart, TooltipComponent, GridComponent, CanvasRenderer, MarkAreaComponent, DataZoomComponent]);

interface ISavingsWidget {
    savings: ISavingsMetrics;
    market: PriceResponseMarket;
    start?: Date;
    end?: Date;
}

export const SavingsWidget = ({ savings, market, start, end }: ISavingsWidget) => {
    const { token: themeColors } = theme.useToken();

    const totalSavings = useMemo(() => {
        return savings.total.reduce((acc, s) => acc + s.value, 0);
    }, [savings.total]);

    // No separate transform needed anymore - just map to [timestamp, value] pairs
    // for a time-based xAxis instead of splitting into parallel arrays.
    const seriesData = useMemo(() => savings.total.map(({ date, value }) => [new Date(date).getTime(), value ?? 0]), [savings.total]);

    const isNoData = seriesData.every(([, value]) => value === 0);

    const option: EChartsOption = {
        grid: {
            top: '40px',
            left: '80px',
            right: '40px',
            bottom: '60px',
        },
        tooltip: {
            trigger: 'axis',
            padding: 0,
            borderColor: '#ffffff',
            axisPointer: {
                type: 'line',
                lineStyle: { type: 'dashed' },
            },
            formatter: (params: any) => {
                const point = Array.isArray(params) ? params.find((p: any) => p.seriesName === 'Savings') : params;
                if (!point) return '';

                const [timestamp, value] = point.value as [number, number];

                const dateString = getTimezoneFormattedTime(new Date(timestamp), market.timezone, DEFAULT_DATE_FORMAT);
                const savingsString = !isNil(value) ? currencyUSDFormatter.format(value) : '-';

                return `<div style="padding: 6px 10px;">
                    <div style="color: #697475; margin-bottom: 4px;">${dateString}</div>
                    <div>${point.marker} Savings: <strong>${savingsString}</strong></div>
                </div>`;
            },
        },
        yAxis: {
            type: 'value',
            splitLine: {
                show: true,
            },
        },
        xAxis: {
            type: 'time',
            axisTick: { show: false },
            min: start && start.getTime(),
            max: end && end.getTime(),
            axisLabel: {
                show: false,
            },
        },
        series: [
            {
                name: 'Savings',
                type: 'line',
                emphasis: { focus: 'series' },
                data: seriesData,
                itemStyle: {
                    color: themeColors.colorPrimary,
                },
            },
            {
                // Invisible helper series pinned to y=0 purely to anchor date
                // labels at a fixed position below the axis, independent of the
                // real line's height at each point.
                name: 'AxisLabels',
                type: 'line',
                data: seriesData.map(([timestamp]) => [timestamp, 0]),
                symbol: 'circle',
                symbolSize: 0, // keeps the symbol as a label-anchor without rendering a visible dot
                silent: true,
                tooltip: { show: false },
                lineStyle: { opacity: 0 },
                emphasis: { disabled: true },
                z: -1,
                label: {
                    show: true,
                    position: 'bottom',
                    distance: 12,
                    color: '#697475',
                    formatter: params => {
                        const timestamp = (params.value as [number, number])[0];

                        return getTimezoneFormattedTime(new Date(timestamp), market.timezone, DEFAULT_DATE_FORMAT);
                    },
                },
                labelLayout: {
                    hideOverlap: true,
                },
            },
        ],
        dataZoom: [
            {
                type: 'inside',
            },
            {
                type: 'slider',
                height: 20,
                bottom: 10,
                labelFormatter: value => getTimezoneFormattedTime(new Date(value), market.timezone),
            },
        ],
    };

    return (
        <Row justify="space-between" gutter={[0, 0]} className="content-background" style={{ padding: '32px 8px', borderRadius: 8 }}>
            <Col xs={24} md={11}>
                <div className="content-row">
                    <Row align="middle" justify="center">
                        <Tooltip title="Savings are based on your performance during the event ">
                            <Space align="center">
                                <MoneyIcon style={{ width: '40px', height: '40px', fill: themeColors.colorPrimary }} />
                                <Typography.Title level={2} style={{ color: themeColors.colorPrimary, margin: 0 }}>
                                    Savings
                                </Typography.Title>
                            </Space>
                        </Tooltip>
                    </Row>
                    <ContentBlock
                        value={currencyUSDFormatter.format(totalSavings)}
                        description="Total USD Saved"
                        descriptionClass="main-content-description"
                        valueClass="main-content-value"
                    />
                </div>
            </Col>
            <Col xs={0} md={2} style={{ display: 'flex', justifyContent: 'center' }}>
                <Divider type="vertical" style={{ height: '100%' }} />
            </Col>
            <Col xs={24} md={0}>
                <Divider style={{ height: '100%' }} />
            </Col>
            <Col xs={24} md={11}>
                <div className="content-row">
                    <ContentBlock
                        value={currencyUSDFormatter.format(savings.avgPerEvent)}
                        description="Average Savings/Event"
                        descriptionClass="content-description"
                        valueClass="content-value"
                    />
                    <ContentBlock
                        value={currencyUSDFormatter.format(savings.avgPerSite)}
                        description="Average Savings/Site"
                        descriptionClass="content-description"
                        valueClass="content-value"
                    />
                </div>
            </Col>
            <Col span={24}>
                {isNoData ? (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                ) : (
                    <ReactEChartsCore
                        echarts={echarts}
                        option={option}
                        onChartReady={instance => {
                            instance.group = CHART_GROUP_ID;
                        }}
                        notMerge
                        lazyUpdate
                    />
                )}
            </Col>
        </Row>
    );
};
